<template>
  <div id="app">
    <!-- 企业微信初始化状态 -->
    <div v-if="!isWechatReady" class="loading-container">
      <div class="loading-content">
        <div class="loading-spinner">
          <i class="el-icon-loading"></i>
        </div>
        <p class="loading-title">正在初始化企业微信...</p>
        <p class="loading-detail">{{ loadingMessage }}</p>
      </div>
    </div>

    <!-- 主应用内容 -->
    <router-view v-else />

    <!-- 调试信息面板 -->
    <div v-if="showDebugPanel" class="debug-panel">
      <div class="debug-header">
        <span>企业微信调试信息</span>
        <el-button type="text" @click="showDebugPanel = false">
          <i class="el-icon-close"></i>
        </el-button>
      </div>
      <div class="debug-content">
        <!-- 重要参数突出显示 -->
        <div class="debug-section highlight-section">
          <h5>🔍 关键调试参数</h5>
          <div class="key-params">
            <div class="param-item">
              <span class="param-label">授权码(code):</span>
              <span class="param-value code-value">{{ $store.state.wechat.code || "未获取" }}</span>
            </div>
            <div class="param-item">
              <span class="param-label">应用ID(agentId):</span>
              <span class="param-value agent-value">{{ $store.state.wechat.agentId || "未获取" }}</span>
            </div>
            <div class="param-item">
              <span class="param-label">前端用户ID:</span>
              <span class="param-value" :class="getFrontendUserId() ? 'success' : 'error'">
                {{ getFrontendUserId() || "未获取" }}
              </span>
            </div>
          </div>
        </div>

        <el-descriptions :column="1" border size="small">
          <el-descriptions-item label="当前地址">{{
            newurl
          }}</el-descriptions-item>
          <el-descriptions-item label="企业ID">{{
            wechatCorpId
          }}</el-descriptions-item>
          <el-descriptions-item label="授权码(code)">{{
            $store.state.wechat.code || "未获取"
          }}</el-descriptions-item>
          <el-descriptions-item label="应用ID(agentId)">{{
            $store.state.wechat.agentId || "未获取"
          }}</el-descriptions-item>
          <el-descriptions-item label="服务端用户ID">{{
            wechatUserId || "未获取"
          }}</el-descriptions-item>
          <el-descriptions-item label="前端用户ID">{{
            getFrontendUserId() || "未获取"
          }}</el-descriptions-item>
          <el-descriptions-item label="SDK版本">{{
            $store.state.wechat.sdkVersion
          }}</el-descriptions-item>
          <el-descriptions-item label="初始化状态">{{
            isWechatReady ? "已完成" : "进行中"
          }}</el-descriptions-item>
        </el-descriptions>

        <!-- 前端用户信息详情 -->
        <div
          class="debug-section"
          v-if="$store.state.wechat.userInfo?.frontend"
        >
          <h5>前端用户信息详情:</h5>
          <div class="user-info-detail">
            <pre>{{
              JSON.stringify($store.state.wechat.userInfo.frontend, null, 2)
            }}</pre>
          </div>
        </div>

        <!-- 服务端用户信息详情 -->
        <div
          class="debug-section"
          v-if="
            $store.state.wechat.userInfo &&
            Object.keys($store.state.wechat.userInfo).length > 0 &&
            !$store.state.wechat.userInfo.frontend
          "
        >
          <h5>服务端用户信息详情:</h5>
          <div class="user-info-detail">
            <pre>{{
              JSON.stringify($store.state.wechat.userInfo, null, 2)
            }}</pre>
          </div>
        </div>

        <!-- 环境信息 -->
        <div
          class="debug-section"
          v-if="
            $store.state.wechat.debugInfo.environment &&
            Object.keys($store.state.wechat.debugInfo.environment).length > 0
          "
        >
          <h5>环境信息:</h5>
          <div class="debug-info-item">
            <span class="label">是否企业微信:</span>
            <span
              :class="
                $store.state.wechat.debugInfo.environment.isWechatWork
                  ? 'success'
                  : 'error'
              "
            >
              {{
                $store.state.wechat.debugInfo.environment.isWechatWork
                  ? "✓ 是"
                  : "✗ 否"
              }}
            </span>
          </div>
          <div class="debug-info-item">
            <span class="label">SDK对象存在:</span>
            <span
              :class="
                $store.state.wechat.debugInfo.environment.wwExists
                  ? 'success'
                  : 'error'
              "
            >
              {{
                $store.state.wechat.debugInfo.environment.wwExists
                  ? "✓ 是"
                  : "✗ 否"
              }}
            </span>
          </div>
          <div class="debug-info-item">
            <span class="label">可用方法数:</span>
            <span>{{
              $store.state.wechat.debugInfo.environment.wwMethods?.length || 0
            }}</span>
          </div>
        </div>

        <!-- 签名信息 -->
        <div
          class="debug-section"
          v-if="
            $store.state.wechat.debugInfo.signatures &&
            Object.keys($store.state.wechat.debugInfo.signatures).length > 0
          "
        >
          <h5>签名信息:</h5>
          <div class="debug-info-item">
            <span class="label">企业签名字段:</span>
            <span>{{
              $store.state.wechat.debugInfo.signatures.corpSignFields?.join(
                ", "
              )
            }}</span>
          </div>
          <div class="debug-info-item">
            <span class="label">应用签名字段:</span>
            <span>{{
              $store.state.wechat.debugInfo.signatures.agentSignFields?.join(
                ", "
              )
            }}</span>
          </div>
          <div
            class="debug-info-item"
            v-if="
              $store.state.wechat.debugInfo.signatures.missingFields?.length > 0
            "
          >
            <span class="label">缺少字段:</span>
            <span class="error">{{
              $store.state.wechat.debugInfo.signatures.missingFields.join(", ")
            }}</span>
          </div>
        </div>

        <!-- SDK注册信息 -->
        <div
          class="debug-section"
          v-if="
            $store.state.wechat.debugInfo.sdkRegistration &&
            Object.keys($store.state.wechat.debugInfo.sdkRegistration).length >
              0
          "
        >
          <h5>SDK注册信息:</h5>
          <div class="debug-info-item">
            <span class="label">注册状态:</span>
            <span
              :class="
                getStatusClass(
                  $store.state.wechat.debugInfo.sdkRegistration.status
                )
              "
            >
              {{ $store.state.wechat.debugInfo.sdkRegistration.status }}
            </span>
          </div>
          <div
            class="debug-info-item"
            v-if="$store.state.wechat.debugInfo.sdkRegistration.functionCheck"
          >
            <span class="label">功能检查:</span>
            <div class="function-check">
              <div
                v-for="(available, funcName) in $store.state.wechat.debugInfo
                  .sdkRegistration.functionCheck"
                :key="funcName"
              >
                <span :class="available ? 'success' : 'error'">
                  {{ available ? "✓" : "✗" }} {{ funcName }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- 调试日志 -->
        <div
          class="debug-section"
          v-if="$store.state.wechat.debugInfo.logs?.length > 0"
        >
          <h5>调试日志:</h5>
          <div class="debug-logs">
            <div
              v-for="(log, index) in $store.state.wechat.debugInfo.logs.slice(
                -10
              )"
              :key="index"
              class="log-item"
            >
              <span class="log-time">{{ log.time }}</span>
              <span :class="'log-level-' + log.level">{{
                log.level.toUpperCase()
              }}</span>
              <span class="log-message">{{ log.message }}</span>
            </div>
          </div>
        </div>

        <div class="debug-requests" v-if="wechatDebugInfo.requests.length > 0">
          <h5>请求记录:</h5>
          <div
            v-for="(req, index) in wechatDebugInfo.requests"
            :key="index"
            class="request-item"
          >
            <div>
              <strong>{{ req.time }}</strong> - {{ req.url }}
            </div>
            <div v-if="req.response" class="success">✓ 成功</div>
            <div v-if="req.error" class="error">✗ {{ req.error }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 调试按钮 -->
    <!-- <el-button
      class="debug-toggle"
      type="primary"
      size="mini"
      circle
      @click="showDebugPanel = !showDebugPanel"
    >
      <i class="el-icon-info"></i>
    </el-button> -->

    <!-- 强制显示按钮（仅在未初始化时显示） -->
    <!-- <el-button
      v-if="!isWechatReady"
      class="force-show-btn"
      type="warning"
      size="mini"
      @click="forceShowApp"
    >
      强制显示
    </el-button> -->
  </div>
</template>

<script>
import * as ww from "@wecom/jssdk";
import { getUserIds, fetchCorpSign, fetchAgentSign } from "@/api/wechat";
import { mapGetters, mapActions } from "vuex";
import { isDebugMode, getDebugConfig } from "@/config/debug.config";

export default {
  name: "App",
  data() {
    return {
      newurl: "",
      loadingMessage: "正在加载...",
      showDebugPanel: false,
      isInitializing: false, // 防止重复初始化
      dataCheckTimer: null, // 数据检查定时器
      isLocalDebug: false, // 是否为本地调试模式
    };
  },
  computed: {
    ...mapGetters([
      "isWechatReady",
      "wechatCorpId",
      "wechatUserId",
      "wechatDebugInfo",
    ]),
  },
  async mounted() {
    console.log("=== App.vue 开始初始化企业微信 ===");
    
    // 检查是否为本地调试模式
    this.isLocalDebug = isDebugMode();
    
    if (this.isLocalDebug) {
      console.log("🔧 本地调试模式已启用，跳过企业微信SDK初始化");
      await this.initializeLocalDebug();
      return;
    }

    // 设置一个超时保护，确保页面能够显示
    const timeoutId = setTimeout(() => {
      if (!this.isWechatReady) {
        this.completeWechatInit();
        this.safeNavigateToOrders();
      }
    }, 10000); // 10秒超时

    try {
      await this.initializeWechat();
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("初始化过程中发生错误:", error);
    }
  },
  beforeDestroy() {
    // 清理定时器
    if (this.dataCheckTimer) {
      clearInterval(this.dataCheckTimer);
      this.dataCheckTimer = null;
    }
  },
  methods: {
    ...mapActions([
      "initWechat",
      "setWechatUserInfo",
      "completeWechatInit",
    ]),

    // 本地调试模式初始化
    async initializeLocalDebug() {
      console.log("🔧 开始本地调试模式初始化...");
      this.loadingMessage = "本地调试模式初始化中...";
      
      const debugConfig = getDebugConfig();
      console.log("🔧 调试配置:", debugConfig);
      
      // 保存调试参数到本地存储
      this.saveWechatParamsToLocal(
        debugConfig.corpId,
        debugConfig.serverUserId,
        debugConfig.externalUserId,
        debugConfig.agentId
      );
      
      // 设置基本信息到store
      this.$store.commit("SET_WECHAT_BASIC_INFO", {
        corpId: debugConfig.corpId,
        agentId: debugConfig.agentId,
        sdkVersion: "本地调试模式",
        code: "debug_mode",
      });
      
      // 设置用户信息到store
      await this.setWechatUserInfo({
        userId: debugConfig.serverUserId,
        userInfo: {
          frontend: {
            userId: debugConfig.externalUserId,
            errMsg: "getCurExternalContact:ok (debug mode)",
          },
          externalUserId: debugConfig.externalUserId,
        },
      });
      
      // 设置getUserId接口调试信息
      this.$store.commit("SET_WECHAT_GETUSERID_DEBUG", {
        url: "本地调试模式",
        requestParams: { debug: true },
        response: debugConfig.serverUserId,
        status: "success (debug mode)",
        timestamp: new Date().toISOString(),
      });
      
      // 完成初始化
      this.completeWechatInit();
      
      console.log("🔧 本地调试模式初始化完成!");
      this.$message.success("本地调试模式已启用");
      
      // 跳转到订单页面
      this.safeNavigateToOrders();
    },

    // 保存企业微信参数到本地存储
    saveWechatParamsToLocal(corpId, serverUserId, externalUserId, agentId) {
      try {
        if (corpId) {
          localStorage.setItem("wechat_corpId", corpId);
          console.log("✓ 保存corpId到本地存储:", corpId);
        }
        if (serverUserId) {
          localStorage.setItem("wechat_serverUserId", serverUserId);
          console.log("✓ 保存serverUserId到本地存储:", serverUserId);
        }
        if (externalUserId) {
          localStorage.setItem("wechat_externalUserId", externalUserId);
          console.log("✓ 保存externalUserId到本地存储:", externalUserId);
        }
        if (agentId) {
          localStorage.setItem("wechat_agentId", agentId);
          console.log("✓ 保存corpId到本地存储:", agentId);
        }

        // 保存时间戳
        localStorage.setItem("wechat_params_timestamp", Date.now().toString());
        console.log("✓ 企业微信参数已保存到本地存储");
      } catch (error) {
        console.error("保存企业微信参数到本地存储失败:", error);
      }
    },


    async initializeWechat() {
      // 防止重复初始化
      if (this.isInitializing) {
        console.log("企业微信正在初始化中，跳过重复调用");
        return;
      }

      // 如果已经初始化完成，也跳过
      if (this.isWechatReady) {
        console.log("企业微信已经初始化完成，跳过重复调用");
        return;
      }

      this.isInitializing = true;
      console.log("开始企业微信初始化...");

      // 在方法顶部定义变量，确保在catch块中也能访问
      let corpId = "";
      let code = "";
      let agentId = "";

      try {
        // 1. 获取URL参数
        this.loadingMessage = "解析URL参数...";
        const urlParams = new URLSearchParams(window.location.search);
        this.newurl = urlParams;
        corpId = urlParams.get("state");
        code = urlParams.get("code");
        agentId = corpId == "wwa731f1a5d453f22f" ? "1000007" : "1000010";
        console.groupEnd();

        // 添加到调试日志
        this.addDebugLog("info", "URL参数解析完成", {
          code: code,
          agentId: agentId,
          corpId: corpId
        });

        // 2. 初始化基本信息
        this.loadingMessage = "初始化基本信息...";

        // 设置SDK版本
        const sdkVersion = ww?.SDK_VERSION || "未知版本";

        // 检查企业微信环境
        const envInfo = {
          userAgent: navigator.userAgent,
          isWechatWork: /wxwork/i.test(navigator.userAgent),
          wwExists: !!ww,
          wwType: typeof ww,
          wwMethods: ww
            ? Object.getOwnPropertyNames(ww).filter(
                (name) => typeof ww[name] === "function"
              )
            : [],
        };
        // 存储环境信息到store
        this.$store.commit("SET_WECHAT_ENVIRONMENT", envInfo);
        this.addDebugLog("info", "环境检查完成", envInfo);

        await this.initWechat({ corpId, code, agentId });

        // 更新SDK版本到store
        this.$store.commit("SET_WECHAT_BASIC_INFO", {
          corpId: corpId,
          agentId: agentId, // 修正为正确的应用ID
          sdkVersion: sdkVersion,
          code: code || "",
        });

        // 保存corpId到本地存储
        this.saveWechatParamsToLocal(corpId, null, null, agentId);

        // 3. 获取签名信息
        this.loadingMessage = "获取签名信息...";
        const signatureUrl = window.location.href.split("#")[0];

        let corpSignData, agentSignData;
        let data, agentData; // 在外层定义变量

        try {
          // 使用统一的API方法获取签名
          corpSignData = await fetchCorpSign(corpId, signatureUrl);
          // this.$message.success("企业配置签名获取成功!");

          // 请求应用配置签名
          try {
            agentSignData = await fetchAgentSign(corpId, agentId, signatureUrl);
          } catch (agentErr) {
            this.$message.warning("应用配置签名接口不可用，将使用企业配置签名");
            console.warn("应用签名请求失败:", agentErr);
            agentSignData = corpSignData;
          }

          // 使用与StatisticsView相同的变量名
          data = corpSignData;
          agentData = agentSignData;

          // 验证签名数据 - 与StatisticsView保持一致
          if (!data.jsapi_ticket || data.jsapi_ticket.length < 40) {
            this.$message.warning("警告: jsapi_ticket长度异常，正常应40位以上");
          }

          if (!data.signature || data.signature.length !== 40) {
            this.$message.warning("警告: signature长度异常，正常应为40位");
          }

          // console.log("签名获取成功:", { data, agentData });

          // 验证签名数据结构
          const sigDebugInfo = {
            corpSignFields: Object.keys(data || {}),
            agentSignFields: Object.keys(agentData || {}),
            corpSignData: data,
            agentSignData: agentData,
          };

          // 检查必需的签名字段
          const requiredFields = ["signature", "noncestr", "timestamp"];
          const missingFields = requiredFields.filter((field) => !data[field]);
          sigDebugInfo.missingFields = missingFields;

          if (missingFields.length > 0) {
            console.warn("企业签名缺少字段:", missingFields);
            this.addDebugLog("warn", "企业签名缺少字段", missingFields);
          }

          // 存储签名调试信息
          this.$store.commit("SET_WECHAT_SIGNATURE_DEBUG", sigDebugInfo);
          this.addDebugLog("info", "签名验证完成", sigDebugInfo);
        } catch (signError) {
          console.error("签名获取失败:", signError);
          this.loadingMessage = `签名获取失败: ${signError.message}`;
          // 即使签名获取失败，也继续尝试获取用户ID
          // this.$message.warning("签名获取失败，部分功能可能不可用");

          // 尝试获取用户ID
          await this.tryGetUserIdOnly(code, corpId);

          // 等待用户ID获取完成
          await this.waitForUserIdAndComplete();
          return;
        }

        // 4. 注册企业微信SDK
        this.loadingMessage = "注册企业微信SDK...";
        if (!ww) {
          console.error("企业微信SDK未加载，ww对象:", ww);
          this.loadingMessage = "企业微信SDK未加载";
          this.$message.error("企业微信SDK未加载，请检查网络连接");

          // 尝试获取用户ID
          await this.tryGetUserIdOnly(code, corpId);

          // 等待用户ID获取完成
          await this.waitForUserIdAndComplete();
          return;
        }

        try {
          const sdkDebugInfo = {
            registerParams: {
              corpId: corpId,
              agentId: agentId, // 修正为正确的应用ID
              signature: corpSignData.signature,
              nonceStr: corpSignData.noncestr,
              timestamp: corpSignData.timestamp,
              jsApiList: [
                "getCurExternalContact",
                "selectExternalContact",
                "getCurExternalChat",
                "getContext",
              ],
            },
            status: "starting",
          };
          this.addDebugLog(
            "info",
            "开始注册企业微信SDK",
            sdkDebugInfo.registerParams
          );


          // 直接调用ww.register，与StatisticsView保持一致
          const registerResult = ww.register({
            corpId: corpId,
            agentId: Number(agentId),
            jsApiList: [
              "getCurExternalContact",
              "getContext",
              "getCurExternalChat",
              "shareToExternalContact",
              "shareToExternalChat",
              "getDeviceInfo",
              "getSystemInfo",
              "getNetworkType",
            ],
            getConfigSignature: () => {
              // this.$message.info("生成企业配置签名...");
              return {
                timestamp: data.timestamp,
                nonceStr: data.noncestr,
                signature: data.signature,
              };
            },
            getAgentConfigSignature: () => {
              // this.$message.info("生成应用配置签名...");
              const signData = agentData || data;
              return {
                timestamp: signData.timestamp,
                nonceStr: signData.noncestr,
                signature: signData.signature,
              };
            },
          });
          console.log("SDK注册调用完成，返回值:", registerResult);

          sdkDebugInfo.registerResult = registerResult;
          sdkDebugInfo.status = "registered";

          // 给SDK一些时间完成注册
          // await new Promise((resolve) => setTimeout(resolve, 1000));
          console.log("SDK注册成功");

          // 验证SDK是否真正可用
          setTimeout(() => {
            console.log("验证SDK功能...");
            const functionCheck = {
              getCurExternalContact:
                typeof ww.getCurExternalContact === "function",
              selectExternalContact:
                typeof ww.selectExternalContact === "function",
              getCurExternalChat: typeof ww.getCurExternalChat === "function",
            };

            sdkDebugInfo.functionCheck = functionCheck;
            sdkDebugInfo.status = "verified";

            if (functionCheck.getCurExternalContact) {
              this.addDebugLog("success", "getCurExternalContact 方法可用");
            } else {
              this.addDebugLog("warn", "getCurExternalContact 方法不可用");
            }

            // 存储完整的SDK调试信息
            this.$store.commit("SET_WECHAT_SDK_DEBUG", sdkDebugInfo);
            this.addDebugLog("info", "SDK注册验证完成", sdkDebugInfo);
          }, 100);
        } catch (sdkError) {
          console.error("SDK注册失败:", sdkError);
          console.error("错误类型:", typeof sdkError);
          console.error("错误详情:", {
            message: sdkError.message,
            stack: sdkError.stack,
            name: sdkError.name,
          });
          this.loadingMessage = `SDK注册失败: ${sdkError.message}`;
          this.$message.warning("SDK注册失败，部分功能可能不可用");
          // 继续初始化
        }

        // 6. 获取用户信息
        this.loadingMessage = "获取用户信息...";

        // 获取服务端用户ID
        if (code && corpId) {
          // 检查是否已经获取过用户ID
          const existingUserId =
            this.$store.state.wechat?.debugInfo?.getUserIdApi?.response;
          if (existingUserId) {
            console.log("服务端用户ID已存在，跳过重复获取:", existingUserId);
            this.addDebugLog("info", "跳过重复获取用户ID", {
              existingUserId: existingUserId,
              reason: "服务端用户ID已存在",
            });
          } else {
            try {
              const serverUserData = await getUserIds({ code, corpId });
              // 存储getUserId接口调试信息
              const getUserIdDebugInfo = {
                url: `/wechat/cp/auth/getUserId?code=${code}&corpId=${corpId}`,
                requestParams: { code, corpId },
                response: serverUserData,
                responseType: typeof serverUserData,
                availableFields: Object.keys(serverUserData || {}),
                status: "success",
                timestamp: new Date().toISOString(),
              };

              this.$store.commit(
                "SET_WECHAT_GETUSERID_DEBUG",
                getUserIdDebugInfo
              );

              // 添加接口返回数据到调试日志
              this.addDebugLog("info", "getUserId接口响应", getUserIdDebugInfo);

              // 直接使用response作为用户ID
              let extractedUserId = null;

              if (serverUserData) {
                // 如果response是字符串，直接使用
                if (typeof serverUserData === "string") {
                  extractedUserId = serverUserData;
                }
                // 如果response是数字，转换为字符串
                else if (typeof serverUserData === "number") {
                  extractedUserId = serverUserData.toString();
                }
                // 如果response是对象，尝试常见字段
                else if (typeof serverUserData === "object") {
                  const possibleUserIdFields = [
                    "userId",
                    "UserId",
                    "userid",
                    "user_id",
                    "id",
                  ];
                  for (const field of possibleUserIdFields) {
                    if (serverUserData[field]) {
                      extractedUserId = serverUserData[field];
                      console.log(
                        `找到用户ID字段: ${field} = ${extractedUserId}`
                      );
                      break;
                    }
                  }

                  // 如果没找到字段，但对象只有一个属性，使用该属性的值
                  if (!extractedUserId) {
                    const keys = Object.keys(serverUserData);
                    if (keys.length === 1) {
                      extractedUserId = serverUserData[keys[0]];
                      console.log(
                        `使用唯一字段作为用户ID: ${keys[0]} = ${extractedUserId}`
                      );
                    }
                  }
                }
              }

              if (extractedUserId) {
                await this.setWechatUserInfo({
                  userId: extractedUserId,
                  userInfo: { originalResponse: serverUserData },
                });

                // 保存到本地存储
                this.saveWechatParamsToLocal(
                  corpId,
                  extractedUserId,
                  null,
                  agentId
                );

                // 更新getUserId接口调试信息 - 成功提取用户ID
                this.$store.commit("SET_WECHAT_GETUSERID_DEBUG", {
                  ...this.$store.state.wechat.debugInfo.getUserIdApi,
                  userIdExtracted: true,
                  extractedUserId: extractedUserId,
                  extractionMethod:
                    typeof serverUserData === "string"
                      ? "direct_string"
                      : typeof serverUserData === "number"
                      ? "direct_number"
                      : "object_field",
                });
              } else {
                // 更新getUserId接口调试信息
                this.$store.commit("SET_WECHAT_GETUSERID_DEBUG", {
                  ...this.$store.state.wechat.debugInfo.getUserIdApi,
                  userIdExtracted: false,
                  userIdExtractionError: `无法从${typeof serverUserData}类型的响应中提取用户ID`,
                  responseType: typeof serverUserData,
                });

                // 添加用户ID解析失败的调试信息
                this.addDebugLog("warn", "getUserId接口用户ID解析失败", {
                  response: serverUserData,
                  responseType: typeof serverUserData,
                  reason: `无法从${typeof serverUserData}类型的响应中提取用户ID`,
                });
              }
            } catch (error) {
              console.error("获取服务端用户信息失败:", error);
              console.error("错误详情:", {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
              });
              // this.$message.warning(`获取用户信息失败: ${error.message}`);

              // 存储getUserId接口失败信息
              const getUserIdErrorInfo = {
                url: `/wechat/cp/auth/getUserId?code=${code}&corpId=${corpId}`,
                requestParams: { code, corpId },
                error: error.message,
                httpStatus: error.response?.status,
                statusText: error.response?.statusText,
                responseData: error.response?.data,
                status: "error",
                timestamp: new Date().toISOString(),
              };

              this.$store.commit(
                "SET_WECHAT_GETUSERID_DEBUG",
                getUserIdErrorInfo
              );

              // 添加接口调用失败的调试信息
              this.addDebugLog(
                "error",
                "getUserId接口调用失败",
                getUserIdErrorInfo
              );
            }
          }
        } else {
          console.warn("缺少code或corpId参数，跳过服务端用户信息获取", {
            code,
            corpId,
          });
        }

        // 获取前端用户ID（外部联系人信息）
        if (ww && typeof ww.getCurExternalContact === "function") {
            console.log("准备获取外部联系人信息...");
            this.addDebugLog("info", "开始调用getCurExternalContact");

            // 使用.then()方式调用，参考官方示例
            ww.getCurExternalContact()
              .then((res) => {
                console.log("getCurExternalContact响应:", res);
                this.addDebugLog("info", "getCurExternalContact响应", res);

                if (res && res.errMsg === "getCurExternalContact:ok") {
                  console.log("✓ 外部联系人信息获取成功:", res);
                  
                  // 在控制台中突出显示前端用户ID获取成功
                  console.log(`%c🎉 前端用户ID获取成功: ${res.userId}`, 'color: #4CAF50; font-weight: bold; font-size: 16px; background: #E8F5E8; padding: 4px 8px; border-radius: 4px;');
                  
                  // this.$message.success(`前端用户ID获取成功: ${res.userId}`);
                  this.addDebugLog("success", "外部联系人信息获取成功", res);

                  // 存储外部联系人信息
                  const serverUserId =
                    this.$store.state.wechat?.debugInfo?.getUserIdApi?.response;
                  this.setWechatUserInfo({
                    userId: res.userId || serverUserId,
                    userInfo: {
                      ...this.$store.state.wechat.userInfo,
                      frontend: res,
                      externalUserId: res.userId, // 专门存储外部联系人ID
                    },
                  });

                  // 保存到本地存储
                  this.saveWechatParamsToLocal(
                    corpId,
                    serverUserId,
                    res.userId,
                    agentId
                  );
                } else {
                  console.warn("✗ 获取外部联系人失败:", res);

                  if (
                    res &&
                    res.errMsg === "getCurExternalContact:no permission"
                  ) {
                    this.$message.error("获取失败: 没有权限调用此接口");
                    this.addDebugLog(
                      "error",
                      "getCurExternalContact权限不足",
                      res
                    );
                  } else if (
                    res &&
                    res.errMsg === "getCurExternalContact:fail"
                  ) {
                    this.$message.warning("获取失败: 可能不在外部联系人会话中");
                    this.addDebugLog("warn", "getCurExternalContact失败", res);
                  } else {
                    this.$message.warning(
                      `获取失败: ${res?.errMsg || "未知错误"} (代码: ${
                        res?.errCode || "无"
                      })`
                    );
                    this.addDebugLog("warn", "getCurExternalContact失败", res);
                  }

                  // 即使失败也存储响应数据用于调试
                  if (res) {
                    const serverUserId =
                      this.$store.state.wechat?.debugInfo?.getUserIdApi
                        ?.response;
                    this.setWechatUserInfo({
                      userId: serverUserId,
                      userInfo: {
                        ...this.$store.state.wechat.userInfo,
                        frontend: res,
                      },
                    });
                  }
                }
              })
              .catch((err) => {
                console.error("getCurExternalContact异常:", err);

                const error = err;
                const errorMsg = error?.errMsg || error?.message || "未知错误";
                this.$message.error(`捕获到错误: ${errorMsg}`);

                if (error?.errMsg === "getCurExternalContact:no permission") {
                  this.addDebugLog(
                    "error",
                    "getCurExternalContact权限不足",
                    error
                  );
                } else {
                  this.addDebugLog("error", "getCurExternalContact异常", {
                    message: error?.message,
                    errMsg: error?.errMsg,
                    errCode: error?.errCode,
                    type: typeof error,
                  });
                }
              });
        } else {
          console.warn(
            "✗ ww.getCurExternalContact方法不可用，跳过前端用户信息获取"
          );
          this.addDebugLog("warn", "getCurExternalContact方法不可用", {
            wwExists: !!ww,
            methodType: typeof ww?.getCurExternalContact,
          });
        }

        // 7. 等待用户ID获取完成
        this.loadingMessage = "等待用户ID获取完成...";
        await this.waitForUserIdAndComplete();

        this.isInitializing = false; // 重置初始化标志
      } catch (error) {
        console.error("企业微信初始化失败:", error);
        this.loadingMessage = `初始化失败: ${error.message}`;
        this.$message.error(`企业微信初始化失败: ${error.message}`);

        // 即使初始化失败，也要尝试获取用户ID
        console.log("尝试fallback初始化...");
        try {
          // 设置基本信息
          this.$store.commit("SET_WECHAT_BASIC_INFO", {
            corpId: corpId,
            agentId: agentId,
            sdkVersion: ww?.SDK_VERSION || "未知版本",
            code: code || "",
          });

          // 尝试获取用户ID
          await this.tryGetUserIdOnly(code, corpId);

          // 等待用户ID获取完成
          await this.waitForUserIdAndComplete();

          console.log("Fallback初始化完成，页面将显示但部分功能可能不可用");
        } catch (fallbackError) {
          console.error("Fallback初始化也失败了:", fallbackError);
          // 最后的保底措施：强制完成初始化
          this.completeWechatInit();
          this.safeNavigateToOrders();
        }
      } finally {
        this.isInitializing = false; // 确保标志被重置
      }
    },

    // 强制显示应用（调试用）
    // 仅尝试获取用户ID（在签名或SDK失败时使用）
    async tryGetUserIdOnly(code, corpId) {
      if (code && corpId) {
        try {
          this.loadingMessage = "尝试获取用户ID...";
          const serverUserData = await getUserIds({ code, corpId });

          // 存储getUserId接口调试信息
          const getUserIdDebugInfo = {
            url: `/wechat/cp/auth/getUserId?code=${code}&corpId=${corpId}`,
            requestParams: { code, corpId },
            response: serverUserData,
            responseType: typeof serverUserData,
            status: "success",
            timestamp: new Date().toISOString(),
          };

          this.$store.commit("SET_WECHAT_GETUSERID_DEBUG", getUserIdDebugInfo);

          // 提取用户ID
          let extractedUserId = null;
          if (typeof serverUserData === "string") {
            extractedUserId = serverUserData;
          } else if (typeof serverUserData === "number") {
            extractedUserId = serverUserData.toString();
          } else if (typeof serverUserData === "object" && serverUserData) {
            const possibleUserIdFields = [
              "userId",
              "UserId",
              "userid",
              "user_id",
              "id",
            ];
            for (const field of possibleUserIdFields) {
              if (serverUserData[field]) {
                extractedUserId = serverUserData[field];
                break;
              }
            }
          }

          if (extractedUserId) {
            await this.setWechatUserInfo({
              userId: extractedUserId,
              userInfo: { originalResponse: serverUserData },
            });

            // 保存到本地存储
            const agentId = corpId == "wwa731f1a5d453f22f" ? "1000007" : "1000010";
            this.saveWechatParamsToLocal(corpId, extractedUserId, null, agentId);

            console.log("✓ 用户ID获取成功:", extractedUserId);
          }
        } catch (error) {
          console.error("获取用户ID失败:", error);
          this.$message.warning(`获取用户ID失败: ${error.message}`);
        }
      }
    },

    // 等待用户ID获取完成并完成初始化 - 优先等待前端用户ID
    async waitForUserIdAndComplete() {
      const maxWaitTime = 8000; // 最大等待8秒
      const checkInterval = 500; // 每500ms检查一次
      let waitedTime = 0;

      return new Promise((resolve) => {
        const checkUserId = () => {
          // 检查是否获取到服务端用户ID
          const hasServerUserId =
            this.$store.state.wechat.debugInfo?.getUserIdApi?.response;
          // 检查是否获取到前端用户ID
          const hasFrontendUserId =
            this.$store.state.wechat.userInfo?.frontend?.userId ||
            this.$store.state.wechat.userInfo?.externalUserId;

          console.log("用户ID检查状态:", {
            serverUserId: hasServerUserId,
            frontendUserId: hasFrontendUserId,
            waitedTime: waitedTime
          });

          // 优先检查前端用户ID，如果有前端用户ID就立即完成初始化
          if (hasFrontendUserId) {
            console.log("✓ 前端用户ID获取成功，立即完成初始化:", hasFrontendUserId);
            this.loadingMessage = "前端用户ID获取成功，完成初始化...";
            this.addDebugLog("success", "前端用户ID获取成功", {
              frontendUserId: hasFrontendUserId
            });

            // 完成初始化
            this.completeWechatInit();
            console.log("=== 企业微信初始化完成 ===");

            // 安全跳转到订单管理页面
            this.safeNavigateToOrders();
            resolve();
            return;
          }
          
          // 如果没有前端用户ID但有服务端用户ID，继续等待前端用户ID
          if (hasServerUserId && !hasFrontendUserId) {
            console.log("已获取服务端用户ID，继续等待前端用户ID...");
            this.loadingMessage = "已获取服务端用户ID，等待前端用户ID...";
          }

          waitedTime += checkInterval;
          if (waitedTime >= maxWaitTime) {
            console.warn("⚠ 前端用户ID获取超时");
            
            if (hasServerUserId) {
              console.log("使用服务端用户ID完成初始化");
              this.loadingMessage = "前端用户ID获取超时，使用服务端用户ID...";
              this.addDebugLog("warn", "前端用户ID获取超时，使用服务端用户ID", {
                serverUserId: hasServerUserId
              });
              
              // 完成初始化但不自动跳转
              this.completeWechatInit();
              // 不调用 safeNavigateToOrders()，因为没有前端用户ID
            } else {
              console.log("未获取到任何用户ID，完成基础初始化");
              this.loadingMessage = "用户ID获取超时，完成基础初始化...";
              this.addDebugLog("warn", "所有用户ID获取超时", {
                reason: "未获取到前端用户ID和服务端用户ID"
              });
              
              // 完成基础初始化
              this.completeWechatInit();
            }
            
            resolve();
            return;
          }

          // 更新加载消息显示等待进度
          const progress = Math.round((waitedTime / maxWaitTime) * 100);
          this.loadingMessage = `等待前端用户ID获取完成... (${progress}%)`;

          // 继续等待
          setTimeout(checkUserId, checkInterval);
        };

        // 开始检查
        checkUserId();
      });
    },

    forceShowApp() {
      console.log("用户手动强制显示应用");
      this.completeWechatInit();
      this.safeNavigateToOrders();
    },

    // 获取前端用户ID
    getFrontendUserId() {
      const frontendUserInfo = this.$store.state.wechat.userInfo?.frontend;
      if (frontendUserInfo) {
        // 尝试多种可能的前端用户ID字段名
        const possibleFields = ["userId", "UserId", "userid", "user_id", "id"];
        for (const field of possibleFields) {
          if (frontendUserInfo[field]) {
            return frontendUserInfo[field];
          }
        }
      }
      return null;
    },

    // 检查必要数据是否完整
    checkDataCompleteness() {
      const corpId = this.$store.state.wechat?.corpId;
      const serverUserId =
        this.$store.state.wechat?.debugInfo?.getUserIdApi?.response;
      const frontendUserId = this.getFrontendUserId();

      console.log("=== 数据完整性检查 ===");
      console.log("企业ID:", corpId);
      console.log("服务端用户ID:", serverUserId);
      console.log("前端用户ID:", frontendUserId);

      const isComplete = !!(corpId && serverUserId && frontendUserId);
      console.log("数据完整性:", isComplete ? "✓ 完整" : "✗ 不完整");

      return {
        isComplete,
        corpId,
        serverUserId,
        frontendUserId,
      };
    },

    // 安全跳转到订单页面 - 只有在获取到前端用户ID时才跳转
    safeNavigateToOrders() {
      const dataCheck = this.checkDataCompleteness();
      
      // 只检查前端用户ID是否存在
      const hasFrontendUserId = !!dataCheck.frontendUserId;

      console.log("=== 跳转条件检查 ===");
      console.log("前端用户ID:", dataCheck.frontendUserId);
      console.log("是否满足跳转条件:", hasFrontendUserId);

      if (hasFrontendUserId) {
        console.log("✓ 前端用户ID已获取，跳转到订单页面");
        console.log(`%c🚀 页面跳转: 前端用户ID验证通过，跳转到订单列表`, 'color: #4CAF50; font-weight: bold; font-size: 14px; background: #E8F5E8; padding: 4px 8px; border-radius: 4px;');
        
        this.addDebugLog("success", "前端用户ID验证通过，跳转到订单页面", {
          frontendUserId: dataCheck.frontendUserId
        });
        
        if (this.$route.path === "/") {
          this.$router.push("/orders");
        }
      } else {
        console.log("✗ 前端用户ID未获取，不进行跳转");
        console.log(`%c⏳ 等待中: 前端用户ID未获取，不进行页面跳转`, 'color: #FF9800; font-weight: bold; font-size: 14px; background: #FFF3E0; padding: 4px 8px; border-radius: 4px;');
        
        this.addDebugLog("warn", "前端用户ID未获取，等待获取完成", {
          corpId: dataCheck.corpId,
          serverUserId: dataCheck.serverUserId,
          frontendUserId: dataCheck.frontendUserId
        });

        // 设置一个定时器，定期检查前端用户ID是否获取到
        this.startDataCheckTimer();
      }
    },

    // 启动数据检查定时器 - 只检查前端用户ID
    startDataCheckTimer() {
      if (this.dataCheckTimer) {
        clearInterval(this.dataCheckTimer);
      }
      let checkCount = 0;
      const maxChecks = 30; // 最多检查30次（30秒）

      this.dataCheckTimer = setInterval(() => {
        checkCount++;
        console.log(`前端用户ID检查第${checkCount}次...`);

        const dataCheck = this.checkDataCompleteness();
        const hasFrontendUserId = !!dataCheck.frontendUserId;

        if (hasFrontendUserId) {
          console.log("✓ 前端用户ID检查完成，跳转到订单页面");
          this.addDebugLog("success", "前端用户ID检查完成，执行跳转", {
            frontendUserId: dataCheck.frontendUserId,
            checkCount: checkCount
          });
          
          clearInterval(this.dataCheckTimer);
          this.dataCheckTimer = null;

          if (this.$route.path === "/") {
            this.$router.push("/orders");
          }
        } else if (checkCount >= maxChecks) {
          console.log("✗ 前端用户ID检查超时，不进行跳转");
          this.addDebugLog("warn", "前端用户ID检查超时，停止检查", {
            checkCount: checkCount,
            maxChecks: maxChecks,
            reason: "未获取到前端用户ID"
          });
          
          clearInterval(this.dataCheckTimer);
          this.dataCheckTimer = null;
          
          // 不进行跳转，保持在当前页面
          console.log("由于未获取到前端用户ID，保持在当前页面");
        } else {
          console.log(`等待前端用户ID... (${checkCount}/${maxChecks})`);
        }
      }, 1000); // 每秒检查一次
    },

    // 添加调试日志
    addDebugLog(level, message, data = null) {
      this.$store.commit("ADD_WECHAT_LOG", { level, message, data });
    },

    // 获取状态对应的CSS类
    getStatusClass(status) {
      switch (status) {
        case "starting":
          return "info";
        case "registered":
          return "warning";
        case "verified":
          return "success";
        default:
          return "info";
      }
    },
  },
};
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  min-height: 100vh;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #f5f5f5;
}

.loading-content {
  text-align: center;
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.loading-spinner {
  font-size: 32px;
  color: #409eff;
  margin-bottom: 20px;
}

.loading-title {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin: 0 0 10px 0;
}

.loading-detail {
  font-size: 14px;
  color: #666;
  margin: 0;
}

/* 调试面板样式 */
.debug-panel {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 400px;
  max-height: 80vh;
  background: white;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  overflow: hidden;
}

.debug-header {
  padding: 12px 16px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 500;
}

.debug-content {
  padding: 16px;
  max-height: 60vh;
  overflow-y: auto;
}

.debug-section {
  margin-top: 16px;
}

.debug-section h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.user-info-detail {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.user-info-detail pre {
  margin: 0;
  font-size: 11px;
  color: #495057;
  white-space: pre-wrap;
  word-break: break-all;
}

.debug-requests {
  margin-top: 16px;
}

.debug-requests h5 {
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #333;
}

.request-item {
  padding: 8px;
  margin-bottom: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
}

.request-item .success {
  color: #67c23a;
  font-weight: 500;
}

.request-item .error {
  color: #f56c6c;
  font-weight: 500;
}

/* 关键参数突出显示样式 */
.highlight-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.highlight-section h5 {
  color: white !important;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
}

.key-params {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  padding: 8px 12px;
  border-radius: 4px;
  backdrop-filter: blur(10px);
}

.param-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
}

.param-value {
  font-weight: 600;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
}

.code-value {
  background: #4CAF50;
  color: white;
}

.agent-value {
  background: #2196F3;
  color: white;
}

/* 调试信息项样式 */
.debug-info-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
}

.debug-info-item .label {
  min-width: 100px;
  color: #666;
  font-weight: 500;
}

.debug-info-item .success {
  color: #67c23a;
  font-weight: 500;
}

.debug-info-item .error {
  color: #f56c6c;
  font-weight: 500;
}

.debug-info-item .warning {
  color: #e6a23c;
  font-weight: 500;
}

.debug-info-item .info {
  color: #409eff;
  font-weight: 500;
}

/* 功能检查样式 */
.function-check {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
}

/* 调试日志样式 */
.debug-logs {
  max-height: 200px;
  overflow-y: auto;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 8px;
}

.log-item {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 11px;
  gap: 8px;
}

.log-time {
  color: #999;
  min-width: 60px;
}

.log-level-info {
  color: #409eff;
  font-weight: 500;
  min-width: 40px;
}

.log-level-warn {
  color: #e6a23c;
  font-weight: 500;
  min-width: 40px;
}

.log-level-error {
  color: #f56c6c;
  font-weight: 500;
  min-width: 40px;
}

.log-level-success {
  color: #67c23a;
  font-weight: 500;
  min-width: 40px;
}

.log-message {
  color: #333;
  flex: 1;
}

/* 调试按钮 */
.debug-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9998;
}

/* 强制显示按钮 */
.force-show-btn {
  position: fixed;
  bottom: 20px;
  right: 80px;
  z-index: 9998;
}

/* 响应式布局 - 桌面端 */
@media (min-width: 768px) {
  .loading-title {
    font-size: 18px;
  }

  .loading-detail {
    font-size: 14px;
  }

  .debug-panel {
    width: 400px;
    right: 20px;
  }

  .debug-header {
    font-size: 16px;
  }

  .debug-content {
    font-size: 14px;
  }

  .debug-toggle {
    bottom: 20px;
    right: 20px;
    width: 40px;
    height: 40px;
  }

  .force-show-btn {
    bottom: 70px;
    right: 20px;
    font-size: 12px;
  }
}

/* 企业微信侧边栏适配 - 移动端 */
@media (max-width: 400px) {
  .loading-container {
    padding: 15px 8px;
  }

  .loading-content {
    margin: 8px;
    padding: 15px 12px;
  }

  .loading-title {
    font-size: 12px;
  }

  .loading-detail {
    font-size: 9px;
  }

  /* 移动端关键参数样式适配 */
  .highlight-section {
    padding: 8px;
    margin-bottom: 12px;
  }

  .highlight-section h5 {
    font-size: 11px !important;
    margin-bottom: 8px !important;
  }

  .param-item {
    padding: 6px 8px;
    font-size: 10px;
  }

  .param-label {
    font-size: 10px;
  }

  .param-value {
    font-size: 10px;
    padding: 1px 6px;
  }

  .debug-panel {
    width: calc(100vw - 20px);
    right: 10px;
    top: 30px;
    max-height: 70vh;
  }

  .debug-header {
    font-size: 11px;
    padding: 6px 10px;
  }

  .debug-content {
    font-size: 9px;
    padding: 6px 10px;
  }

  .debug-info-item {
    font-size: 9px;
    margin-bottom: 4px;
  }

  .debug-section h5 {
    font-size: 10px;
    margin-bottom: 6px;
  }

  .user-info-detail pre {
    font-size: 8px;
    padding: 4px;
  }

  .debug-toggle {
    bottom: 15px;
    right: 15px;
    width: 32px;
    height: 32px;
  }

  .force-show-btn {
    bottom: 55px;
    right: 15px;
    font-size: 10px;
    padding: 4px 8px;
  }

  .el-descriptions {
    font-size: 9px;
  }

  .el-descriptions-item__label {
    font-size: 9px;
  }

  .el-descriptions-item__content {
    font-size: 9px;
  }

  /* Element UI 组件字体大小覆盖 */
  .el-button {
    font-size: 9px !important;
  }

  .el-input__inner {
    font-size: 9px !important;
  }

  .el-form-item__label {
    font-size: 9px !important;
  }

  .el-tab-pane {
    font-size: 9px !important;
  }

  .el-tabs__item {
    font-size: 9px !important;
  }

  .el-tag {
    font-size: 8px !important;
  }

  .el-pagination {
    font-size: 9px !important;
  }

  .el-empty__description {
    font-size: 9px !important;
  }

  .el-message {
    font-size: 9px !important;
  }

  .el-dialog__title {
    font-size: 10px !important;
  }

  .el-dialog__body {
    font-size: 9px !important;
  }
}

/* 中等屏幕适配 */
@media (min-width: 401px) and (max-width: 767px) {
  .loading-content {
    margin: 15px;
    padding: 25px 18px;
  }

  .loading-title {
    font-size: 16px;
  }

  .loading-detail {
    font-size: 13px;
  }

  .debug-panel {
    width: calc(100vw - 30px);
    right: 15px;
  }

  .debug-header {
    font-size: 14px;
  }

  .debug-content {
    font-size: 13px;
  }

  .debug-toggle {
    bottom: 18px;
    right: 18px;
    width: 36px;
    height: 36px;
  }

  .force-show-btn {
    bottom: 62px;
    right: 18px;
    font-size: 11px;
  }
}
</style>
